/**
 * Font definitions for the Canvas Text Editor.
 * 12 curated Google Fonts across display, serif, script, and mono categories.
 */

export const CANVAS_FONTS = [
  // Already loaded in index.css
  { key: 'inter',           family: 'Inter',             category: 'sans',    loaded: true },
  { key: 'oswald',          family: 'Oswald',            category: 'display', loaded: true },
  { key: 'jetbrains',       family: 'JetBrains Mono',    category: 'mono',    loaded: true },
  // Need dynamic loading
  { key: 'playfair',        family: 'Playfair Display',  category: 'serif' },
  { key: 'bebas',           family: 'Bebas Neue',        category: 'display' },
  { key: 'permanent-marker',family: 'Permanent Marker',  category: 'marker' },
  { key: 'dancing',         family: 'Dancing Script',    category: 'script' },
  { key: 'righteous',       family: 'Righteous',         category: 'display' },
  { key: 'abril',           family: 'Abril Fatface',     category: 'serif' },
  { key: 'satisfy',         family: 'Satisfy',           category: 'script' },
  { key: 'anton',           family: 'Anton',             category: 'display' },
  { key: 'lobster',         family: 'Lobster',           category: 'script' },
];

// Track which fonts have been loaded to avoid duplicate requests
const loadedFonts = new Set(['Inter', 'Oswald', 'JetBrains Mono']);

/**
 * Dynamically load a Google Font by family name.
 * Returns a promise that resolves when the font is ready.
 */
export async function loadFont(family) {
  if (loadedFonts.has(family)) return;
  loadedFonts.add(family);

  const encoded = family.replace(/\s+/g, '+');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;700&display=swap`;
  document.head.appendChild(link);

  // Wait for font to actually load
  try {
    await document.fonts.load(`400 48px "${family}"`);
    await document.fonts.load(`700 48px "${family}"`);
  } catch {
    // Font API not supported — link tag will handle it
  }
}

/**
 * Preload all canvas fonts. Called once when TextPanel opens.
 */
export function preloadAllFonts() {
  CANVAS_FONTS.forEach((f) => {
    if (!f.loaded) loadFont(f.family);
  });
}

// Text color presets
export const TEXT_COLORS = [
  '#FFFFFF', '#000000', '#FF3B30', '#FF9500', '#FFCC00',
  '#34C759', '#007AFF', '#AF52DE', '#FF2D55', '#8E8E93',
  '#F5E6D3', '#D4A574', '#8B6914', '#C7956D',
];

// Text effect definitions
export const TEXT_EFFECTS = [
  { key: 'none',    label: 'None' },
  { key: 'shadow',  label: 'Shadow' },
  { key: 'outline', label: 'Outline' },
  { key: 'glow',    label: 'Glow' },
  { key: 'lifted',  label: 'Lifted' },
];

// 8 preset text styles
export const TEXT_PRESETS = [
  {
    key: 'meme-bold',
    label: 'Meme Bold',
    fontFamily: 'Anton',
    fontSize: 72,
    fill: '#FFFFFF',
    fontWeight: '400',
    textAlign: 'center',
    charSpacing: 40,
    effect: 'outline',
  },
  {
    key: 'neon-glow',
    label: 'Neon Glow',
    fontFamily: 'Righteous',
    fontSize: 56,
    fill: '#00FFAA',
    fontWeight: '400',
    textAlign: 'center',
    charSpacing: 80,
    effect: 'glow',
  },
  {
    key: 'clean-serif',
    label: 'Clean Serif',
    fontFamily: 'Playfair Display',
    fontSize: 48,
    fill: '#FFFFFF',
    fontWeight: '700',
    textAlign: 'center',
    charSpacing: 20,
    effect: 'none',
  },
  {
    key: 'street-tag',
    label: 'Street Tag',
    fontFamily: 'Permanent Marker',
    fontSize: 64,
    fill: '#FFCC00',
    fontWeight: '400',
    textAlign: 'center',
    charSpacing: 0,
    effect: 'shadow',
  },
  {
    key: 'elegant',
    label: 'Elegant',
    fontFamily: 'Dancing Script',
    fontSize: 56,
    fill: '#F5E6D3',
    fontWeight: '700',
    textAlign: 'center',
    charSpacing: 20,
    effect: 'none',
  },
  {
    key: 'retro-display',
    label: 'Retro',
    fontFamily: 'Lobster',
    fontSize: 60,
    fill: '#FF9500',
    fontWeight: '400',
    textAlign: 'center',
    charSpacing: 0,
    effect: 'lifted',
  },
  {
    key: 'code-mono',
    label: 'Code',
    fontFamily: 'JetBrains Mono',
    fontSize: 36,
    fill: '#34C759',
    fontWeight: '400',
    textAlign: 'left',
    charSpacing: 0,
    effect: 'none',
  },
  {
    key: 'headline',
    label: 'Headline',
    fontFamily: 'Oswald',
    fontSize: 64,
    fill: '#FFFFFF',
    fontWeight: '700',
    textAlign: 'center',
    charSpacing: 120,
    effect: 'shadow',
  },
];

/**
 * Apply a text effect to a Fabric.js text object.
 */
export function applyTextEffect(textObj, effectKey) {
  // Clear existing effects
  textObj.set({
    shadow: null,
    stroke: null,
    strokeWidth: 0,
    paintFirst: 'fill',
  });

  switch (effectKey) {
    case 'shadow':
      textObj.set({
        shadow: '3px 3px 6px rgba(0,0,0,0.6)',
      });
      break;
    case 'outline':
      textObj.set({
        stroke: '#000000',
        strokeWidth: 2,
        paintFirst: 'stroke',
      });
      break;
    case 'glow':
      textObj.set({
        shadow: '0px 0px 20px ' + (textObj.fill || '#FFFFFF'),
      });
      break;
    case 'lifted':
      textObj.set({
        shadow: '4px 8px 12px rgba(0,0,0,0.4)',
      });
      break;
    default:
      break;
  }
}
